// Story 5.4 — TanStack Query read hook for GET
// /api/v1/marketplace/share-listings.
//
// URL-driven SPA pattern: MarketplacePage owns URL → args mapping; this
// hook is a pure function of its arguments. queryKey includes every
// filter param so distinct combinations cache separately. 30s staleTime
// mirrors Story 4.2 useVaultItems.
//
// Array params (creators, tiers) are encoded as comma-separated strings.
// null price params are omitted entirely.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type ShareTier = "common" | "preferred" | "premium" | "founders";
export type MarketplaceBrowseSort =
  | "price_asc"
  | "price_desc"
  | "listed_at_desc";
export type MarketplacePerPage = 24 | 36 | 48;

export interface UseMarketplaceShareListingsArgs {
  page: number;
  perPage: MarketplacePerPage;
  creators: string[];
  tiers: ShareTier[];
  priceMinCents: number | null;
  priceMaxCents: number | null;
  sort: MarketplaceBrowseSort;
  enabled?: boolean;
}

export interface MarketplaceShareCardData {
  share_id: string;
  creator_user_id: string;
  creator_handle: string | null;
  creator_display_name: string;
  creator_image_signed_url: string | null;
  tier: ShareTier;
  edition_number: number;
  share_serial: number;
  drop_id: string;
  drop_price_cents: number;
  snapshot_image_signed_url: string | null;
  // Epic 8 — full certificate face. portfolio_title = originating dossier
  // name; opened_at = when the dossier was revealed; condition (0..1, higher
  // = more worn) + signed drive the procedural wear + signature scrawl.
  portfolio_title: string;
  opened_at: string;
  condition: number;
  signed: boolean;
}

export interface MarketplaceShareListingRow {
  listing_id: string;
  price_cents: number;
  listed_at: string;
  expires_at: string | null;
  seller_anonymous: boolean;
  seller_handle: string | null;
  delta_vs_floor_cents: number;
  is_floor: boolean;
  /** Whether the seller accepts below-asking offers (Story 5.10). Gates the
   *  "Make an offer" CTA. */
  allow_bidding: boolean;
  /** True when this listing is the viewer's own — the SPA hides Buy +
   *  Make-an-offer so you can't accidentally buy/bid on your own listing. */
  is_own_listing: boolean;
  share: MarketplaceShareCardData;
}

export interface MarketplacePageInfo {
  total_in_page: number;
  page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface AvailableCreator {
  id: string;
  display_name: string;
  image_signed_url: string | null;
  active_listing_count: number;
}

export interface ShareListingsFilterMetadata {
  min_price_cents: number | null;
  max_price_cents: number | null;
  available_creators: AvailableCreator[];
}

export interface MarketplaceShareListingsResponseData {
  data: MarketplaceShareListingRow[];
  page_info: MarketplacePageInfo;
  filter_metadata: ShareListingsFilterMetadata;
}

export interface MarketplaceShareListingsError {
  code: string;
  message: string;
  field?: string;
  status: number;
}

function buildShareListingsUrl(args: UseMarketplaceShareListingsArgs): string {
  const sp = new URLSearchParams();
  sp.set("page", String(args.page));
  sp.set("per_page", String(args.perPage));
  if (args.creators.length > 0) {
    sp.set("creator", args.creators.join(","));
  }
  if (args.tiers.length > 0) {
    sp.set("tier", args.tiers.join(","));
  }
  if (args.priceMinCents != null) {
    sp.set("price_min", String(args.priceMinCents));
  }
  if (args.priceMaxCents != null) {
    sp.set("price_max", String(args.priceMaxCents));
  }
  sp.set("sort", args.sort);
  return `/api/v1/marketplace/share-listings?${sp.toString()}`;
}

async function fetchShareListings(
  args: UseMarketplaceShareListingsArgs,
): Promise<MarketplaceShareListingsResponseData> {
  const url = buildShareListingsUrl(args);
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: MarketplaceShareListingsError = {
      code: "BROWSE_FAILED",
      message: `Marketplace browse failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  const body = (await response.json()) as {
    data: MarketplaceShareListingsResponseData;
  };
  return body.data;
}

export function useMarketplaceShareListings(
  args: UseMarketplaceShareListingsArgs,
): UseQueryResult<
  MarketplaceShareListingsResponseData,
  MarketplaceShareListingsError
> {
  return useQuery<
    MarketplaceShareListingsResponseData,
    MarketplaceShareListingsError
  >({
    queryKey: [
      "marketplace-share-listings",
      args.page,
      args.perPage,
      [...args.creators].sort(),
      [...args.tiers].sort(),
      args.priceMinCents,
      args.priceMaxCents,
      args.sort,
    ],
    queryFn: () => fetchShareListings(args),
    staleTime: 30 * 1000,
    enabled: args.enabled !== false,
    // Fail fast: a failing browse should surface an error immediately, not
    // sit on "Loading…" through 3 exponential-backoff retries.
    retry: false,
  });
}
